import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { DateService } from 'src/app/services/date.service';
import { GlobalConstants } from 'src/app/services/global-constants';
import { NotificationHandlerService } from 'src/app/services/notification-handler.service';
import { PermissionHandlerService } from 'src/app/services/permission-handler.service';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss']
})
export class NavComponent {

  viewNotification: boolean = false;
  notifications: any[] = [];
  staticFileUrl: string = GlobalConstants.staticFilesURL;
  newNotificationCount: number = 0;
  externalCoordinator: any
  isLogin = false
  productServiceCount: number = 0;
  // Guards the auto "mark all as seen" call so we only fire it once per
  // dropdown-open transition (and never twice in flight).
  private markingAllSeen: boolean = false;

  constructor(
    private router: Router,
    private dataService: DataService,
    private notificationHandler: NotificationHandlerService,
    private dateService: DateService,
    private permissionService: PermissionHandlerService
  ) { }

  hasOpenLibraryPermission() {
    return this.permissionService.payload.kLibrary.allow;
  }

  hasOpenViewPermission() {
    return this.permissionService.payload.dataView.allow;
  }

  ngOnInit() {

    if (localStorage.getItem('isLoggedIn') == 'true') {
      this.isLogin = true
    }

    this.getNotificaitons();
    this.getProductServiceLength();
    this.notificationHandler.getNotifications().subscribe(notificaiton => {
      if (!this.isSeen(notificaiton)) {
        this.newNotificationCount += 1;
      }
      this.notifications.unshift(notificaiton);
    });

    let coordinator = localStorage.getItem('externalUser')
    this.externalCoordinator = coordinator ? JSON.parse(coordinator) : false
  }

  checkIsImage(img: string) {
    return this.notificationHandler.checkisImg(img);
  }

  hasPermission(unit: string, flag: string) {
    return this.permissionService.hasPermission(unit, flag);
  }

  handleLogout() {
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  async getNotificaitons() {
    if (this.notifications.length == 0) {
      try {
        let ous_allowed = localStorage.getItem('all_ou');
        let res = await this.dataService.postData('notification', { ous: ous_allowed ? JSON.parse(ous_allowed) : [] });
        if (!res.error) {
          this.notifications = res.data.filter((n: any) => (this.notificationHandler.applyPermissions(n) && this.notificationHandler.applyDatePermission(n)))
            .map((n: any) => {
              if (!this.isSeen(n)) {
                this.newNotificationCount += 1;
              }
              return this.notificationHandler.setNotificationTypeMessage(n);
            });
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  getDateDifference(date: any) {
    return this.dateService.getDataDiff(date, new Date())
  }


  isSeen(notification: any): boolean {
    const userId = localStorage.getItem('uid');
    if (!userId) {
      return false;
    }
    if (!Array.isArray(notification?.seenBy)) {
      return false;
    }
    return notification.seenBy.includes(userId);
  }

  isHomePage(): boolean {
    return this.router.url === '/home';
  }

  getNavbarClasses(): string {
    return this.isHomePage() ? 'navbar bg-body-tertiary align-items-end home-navbar' : 'navbar bg-body-tertiary align-items-end';
  }

  navigateToFav() {
    let queryParams = { searchTerm: 'fav' };
    this.router.navigate(['/search-view'], { queryParams })
  }

  getFavoriteCount(): string {
    let favorites = JSON.parse(localStorage.getItem('favoriteData') || '[]');
    return favorites.length.toString();
  }


  /**
   * Mark a single notification as seen and navigate to its target. The local
   * state is updated OPTIMISTICALLY (before the network round-trip) so the
   * user sees instant feedback — that specific row visibly fades from
   * "unread" to "read" and the bell badge count decreases by one. If the API
   * call later fails we revert the optimistic update so the count and the
   * row's styling reflect reality.
   *
   * The `seenBy` array is defensively initialised first because some legacy
   * documents in the database have come back with a missing/null value, which
   * caused the optimistic decrement to silently no-op for those rows.
   */
  async markSeen(notification: any) {
    if (!notification) {
      return;
    }
    const userId = localStorage.getItem('uid');
    if (!Array.isArray(notification.seenBy)) {
      notification.seenBy = [];
    }
    if (userId && notification.seenBy.includes(userId)) {
      this.notificationHandler.navigateToRoute(notification);
      return;
    }

    const previousSeenBy = [...notification.seenBy];
    const previousCount = this.newNotificationCount;
    let optimisticallyApplied = false;
    if (userId) {
      notification.seenBy.push(userId);
      this.newNotificationCount = Math.max(0, this.newNotificationCount - 1);
      optimisticallyApplied = true;
    }
    this.notificationHandler.navigateToRoute(notification);
    try {
      const res = await this.dataService.postData('notification/' + notification._id, {}, 'PUT', false);
      if (res?.error && optimisticallyApplied) {
        notification.seenBy = previousSeenBy;
        this.newNotificationCount = previousCount;
      }
    } catch (e) {
      console.warn('Failed to mark notification as seen', e);
      if (optimisticallyApplied) {
        notification.seenBy = previousSeenBy;
        this.newNotificationCount = previousCount;
      }
    }
  }

  /**
   * Toggle the notification panel. Opening the panel only reveals the list —
   * it does NOT auto-mark every notification as seen. The user explicitly
   * acknowledges each notification by clicking it (which calls `markSeen` and
   * decrements the badge by one), so the per-click visual feedback (count
   * goes down, that row fades) is preserved. `markAllSeen` is still available
   * for the dedicated "all notifications" page where the user is intentionally
   * reviewing the entire history.
   */
  toggleNotificationPanel() {
    this.viewNotification = !this.viewNotification;
  }

  /**
   * Bulk mark every notification as seen for this user. Updates local state
   * optimistically so the badge and per-row styling reflect "seen" without
   * waiting for the network round-trip; on error we revert.
   */
  async markAllSeen() {
    if (this.markingAllSeen) {
      return;
    }
    if (this.newNotificationCount <= 0) {
      return;
    }
    this.markingAllSeen = true;
    const userId = localStorage.getItem('uid');
    const previousCount = this.newNotificationCount;
    const optimisticallyMarked: any[] = [];
    try {
      if (userId) {
        for (const n of this.notifications) {
          if (Array.isArray(n?.seenBy) && !n.seenBy.includes(userId)) {
            n.seenBy.push(userId);
            optimisticallyMarked.push(n);
          }
        }
      }
      this.newNotificationCount = 0;

      const res = await this.dataService.postData('notification/seen-all', {}, 'PUT', false);
      if (res?.error) {
        if (userId) {
          for (const n of optimisticallyMarked) {
            n.seenBy = (n.seenBy || []).filter((id: string) => id !== userId);
          }
        }
        this.newNotificationCount = previousCount;
      }
    } catch (e) {
      console.warn('Failed to mark all notifications as seen', e);
      if (userId) {
        for (const n of optimisticallyMarked) {
          n.seenBy = (n.seenBy || []).filter((id: string) => id !== userId);
        }
      }
      this.newNotificationCount = previousCount;
    } finally {
      this.markingAllSeen = false;
    }
  }

  async getProductServiceLength() {
    try {
      let res = await this.dataService.getData('product-service/length/active/count');
      if (!res.error) {
        this.productServiceCount = res.data;
      }
    } catch (e) {
      console.log(e);
    }
  }
}
